import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { makeStyles, TextField, Box, Typography, Button, Link, Modal, Grid } from '@material-ui/core';
import { ArrowBackIcon } from 'components/icons';
import { useTranslation } from 'react-i18next';
import { PropertyDropdown } from 'components';
import { useProperties, useOrganizations } from 'hooks';
import { DashboardHelpers } from 'utils';
import _ from 'lodash';

import { DeleteConfirmPrompt } from 'components';
import palette from 'theme/palette';

const useStyles = makeStyles((theme) => ({
  backIcon: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  intro: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  button: {
    width: '100%',
    fontWeight: 300,
    fontSize: '20px',
    lineHeight: '24px',
    textTransform: 'unset',
  },

  formGroupGrid: {
    width: '100%',
    flexGrow: 1,
  },
}));

const FieldSideBarEdit = ({
  field,
  editPolygon,
  onSave,
  setValues,
  onPrev,
  selectedFieldId,
  deleteField,
  updateField,
}) => {
  const [showFieldDeleteModal, setShowFieldDeleteModal] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const { properties, selectedProperty } = useProperties();
  const { selectedOrganization } = useOrganizations();
  const [fieldEditErrors, setFieldEditErrors] = useState({
    name: '',
    property_id: '',
  });
  const [localSelectedProperty, setLocalSelectedProperty] = useState(
    field.property_id !== DashboardHelpers.StatusCodes.NoPropertySelected &&
      !!field.property_id &&
      field.property_id !== ''
      ? _.find(properties, { id: field.property_id })
      : !!selectedProperty && selectedProperty !== DashboardHelpers.StatusCodes.NoPropertySelected
        ? selectedProperty
        : { id: DashboardHelpers.StatusCodes.NoPropertySelected, name: t('none_selected') }
  );

  const selectProperty = (property) => {
    let fieldError = fieldEditErrors;
    if (property.id !== localSelectedProperty.id) {
      setLocalSelectedProperty(property);
      setValues({ ...field, property_id: property.id });
      fieldError['property_id'] = '';
    } else {
      setValues({ ...field, property_id: property.id });
      fieldError['property_id'] = '';
    }
    setFieldEditErrors(fieldError);
  };

  const handleFieldChange = (event, name, value) => {
    if (name == 'sand' || name == 'silt' || name == 'clay') {
      if (Number(value) <= 100 && texturesAreValid(name, value)) {
        let newTexture = soil_texture;
        newTexture[name] = Number(value);
        setSoilTexture(newTexture);
        setValues({ ...field, soil_texture: newTexture });
      }
    } else if (name == 'soil_density') {
      let newDensity = value;
      setSoilDensity(newDensity);
      setValues({ ...field, [name]: Number(newDensity) });
    } else if (event.persist) {
      setValues({ ...field, [name]: value });
    }
  };

  const handleFinishEditPolygon = () => {
    setValues({ ...field, editPolygon: false });
  };

  const deleteConfirmed = () => {
    deleteField(selectedFieldId);
    updateField();
  };

  const handleDeleteField = () => {
    setShowFieldDeleteModal(true);
  };
  const lostFocus = (event, field) => {
    const realTimeValue = event.target.value;
    let fieldError = { name: '', property_id: '' };
    if (realTimeValue === '' || realTimeValue === undefined) {
      fieldError[field] = t('required');
    }
    if (
      localSelectedProperty.id === DashboardHelpers.StatusCodes.NoPropertySelected ||
      localSelectedProperty === undefined ||
      localSelectedProperty.id !== ''
    ) {
      fieldError['property_id'] = t('required');
    }
    setFieldEditErrors(fieldError);
  };

  console.log('field.property_id', field.property_id);
  const showSubmit =
    field.name !== undefined &&
    field.name !== '' &&
    field.property_id !== DashboardHelpers.StatusCodes.NoPropertySelected &&
    field.property_id !== undefined &&
    field.property_id !== '';

  const [soil_texture, setSoilTexture] = useState({ sand: Number(''), silt: Number(''), clay: Number('') });
  const [soil_density, setSoilDensity] = useState();
  const [showTextureError, setShowTextureError] = useState(false);

  useEffect(() => {
    if (field?.soil_density) {
      setSoilDensity(field.soil_density);
    } else {
      setSoilDensity();
    }

    if (field?.soil_texture) {
      setSoilTexture(field.soil_texture);
    } else {
      setSoilTexture({ sand: Number(''), silt: Number(''), clay: Number('') });
    }
  }, []);

  const texturesAreValid = (name, value) => {
    let newTexture = soil_texture;
    newTexture[name] = Number(value);
    if (Number(newTexture['clay']) + Number(newTexture['sand']) + Number(newTexture['silt']) <= 100) {
      setShowTextureError(false);
      return true;
    } else {
      setShowTextureError(true);
      return false;
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Modal
        open={showFieldDeleteModal}
        onClose={() => {}}
        aria-labelledby="modal-organization-edit"
        aria-describedby="modal-organization-edit-form"
      >
        <div>
          <DeleteConfirmPrompt
            show={showFieldDeleteModal}
            data={field}
            deleteConfirm={deleteConfirmed}
            hide={() => setShowFieldDeleteModal(false)}
          />
        </div>
      </Modal>
      <Box>
        <Link onClick={onPrev} className={classes.backIcon}>
          <ArrowBackIcon size="small" />
          <Typography style={{ paddingLeft: 8 }}>{t('how_add_fields')}</Typography>
        </Link>
      </Box>
      <Box pb={2}>
        <Typography variant="h4" className={classes.intro}>
          {t('edit_field')}
        </Typography>
      </Box>
      <Box pb={2}>
        <div className={classes.formGroupGrid}>
          <PropertyDropdown
            selectedProperty={localSelectedProperty}
            setSelectedProperty={selectProperty}
            properties={properties}
            selectedOrganization={selectedOrganization}
            required={true}
            helperText={fieldEditErrors.property_id}
          />
        </div>
      </Box>
      <Box pb={3} pt={3}>
        <div className={classes.formGroupGrid}>
          <Typography variant="subtitle2" gutterBottom component="div">
            {t('field_name')}
          </Typography>
          <TextField
            fullWidth
            label={t('field_name')}
            name="name"
            size="small"
            autoComplete="name"
            onBlur={(event) => lostFocus(event, 'name')}
            onChange={(event) => handleFieldChange(event, 'name', event.target.value)}
            value={field.name}
            placeholder={t('field_name')}
            helperText={fieldEditErrors.name}
            inputProps={{
              maxLength: 24,
            }}
            required
          />
        </div>

        <Grid item container xs={12}>
          <div>
            <TextField
              fullWidth
              label={t('soil_density')}
              name="soil_density"
              size="small"
              autoComplete="soil_density"
              onChange={(event) => handleFieldChange(event, 'soil_density', event.target.value)}
              value={soil_density}
              placeholder={t('soil_density')}
              inputProps={{
                maxLength: 24,
              }}
              required
            />
          </div>
        </Grid>

        <Grid item container xs={12}>
          <div>
            <Typography variant="subtitle2" gutterBottom component="div">
              {t('soil_texture')}
            </Typography>
          </div>
          {showTextureError && (
            <Grid item container xs={12}>
              <Typography style={{ color: 'red' }} variant="subtitle2" gutterBottom component="div">
                Clay, sand, and silt must total 100 or less.
              </Typography>
            </Grid>
          )}

          <Grid item container xs={12}>
            <TextField
              fullWidth
              label={t('sand')}
              name="sand"
              size="small"
              autoComplete="sand"
              onChange={(event) => handleFieldChange(event, 'sand', event.target.value)}
              value={soil_texture['sand']}
              placeholder={t('sand')}
              inputProps={{
                maxLength: 24,
              }}
              required
            />
          </Grid>
          <Grid item container xs={12}>
            <TextField
              fullWidth
              label={t('silt')}
              name="silt"
              size="small"
              autoComplete="silt"
              onChange={(event) => handleFieldChange(event, 'silt', event.target.value)}
              value={soil_texture['silt']}
              placeholder={t('silt')}
              inputProps={{
                maxLength: 24,
              }}
              required
            />
          </Grid>
          <Grid item container xs={12}>
            <TextField
              fullWidth
              label={t('clay')}
              name="clay"
              size="small"
              autoComplete="clay"
              onChange={(event) => handleFieldChange(event, 'clay', event.target.value)}
              value={soil_texture['clay']}
              placeholder={t('clay')}
              inputProps={{
                maxLength: 24,
              }}
              required
            />
          </Grid>
        </Grid>
      </Box>
      <Box flexGrow={1}>
        {field.editPolygon ? (
          <Button variant="contained" disableElevation key="edit" onClick={handleFinishEditPolygon}>
            {t('finish_edit_region')}
          </Button>
        ) : (
          <Button variant="contained" disableElevation key="edit" onClick={editPolygon}>
            {t('edit_region')}
          </Button>
        )}
        <div style={{ margin: 12 }} />
        <Button
          variant="contained"
          disableElevation
          style={{ backgroundColor: palette.error.main, color: 'white' }}
          key="delete"
          onClick={handleDeleteField}
        >
          {t('delete_field')}
        </Button>
      </Box>
      <Box>
        <Button
          disabled={!showSubmit || showTextureError}
          color="primary"
          variant="contained"
          className={classes.button}
          onClick={onSave}
        >
          {t('save')}
        </Button>
      </Box>
    </Box>
  );
};

FieldSideBarEdit.propTypes = {
  onSave: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  field: PropTypes.object.isRequired,
  editPolygon: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  selectedFieldId: PropTypes.string,
  deleteField: PropTypes.func.isRequired,
  updateField: PropTypes.func.isRequired,
};

FieldSideBarEdit.defaultProps = {
  selectedFieldId: undefined,
};

export default FieldSideBarEdit;
